import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import {
  selectPayrollCompany,
  selectPayrollProfile,
} from '../payroll.selectors'
import { Text, Card, Button, Icon, Image, Link, CardColors } from '@/ui'
import { useState, useEffect } from 'react'
import {
  fetchPayrollProfileIfNeeded,
  fetchPayrollWcEnrollmentRequirements,
} from '../payrollActions'
import {
  GOVERNMENT,
  NEXT,
  TEXAS,
  WcEnrollmentRequirement,
} from '../payrollWcEnrollmentRequirement.slice'
import { WorkersCompPanel } from './WorkersCompPanel'
import { WorkersCompModal } from './WorkersCompModal'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

const stateToCopyMapping: { [key: string]: string } = {
  ND: "This state's program in managed by the government and not a private carrier and therefore is not supported/paid by Heard or our workers' comp partner. Please call the state directly at (800) 777-5033.",
  OH: "This state's program in managed by the government and not a private carrier and therefore is not supported/paid by Heard or our workers' comp partner. Please call the state directly at (800) 644-6292.",
}
const GUSTO_WC_ARTICLE =
  'https://support.gusto.com/article/106950575100000/Workers-Compensation-requirements-by-state'

const LogoRow = () => (
  <div className="inline-flex items-center">
    <Image
      src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
      alt="heard logo"
      className="h-[18px] inline"
    />
    <Icon icon={faPlus} color="green" className="ml-2 inline" />
    <Image
      src="https://heard-images.s3.us-west-1.amazonaws.com/assets/next_logo.png"
      alt="next logo"
      className="h-[18px] inline pl-2"
    />
  </div>
)

const EnrollmentButtons = () => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="w-full md:w-5/12 flex flex-col justify-center">
      <WorkersCompPanel buttonText="Get Started" />
      <div className="mt-2">
        <Button
          variant="secondary"
          onClick={() => setIsOpen(true)}
          className="w-full"
        >
          I already have this
        </Button>
      </div>
      <WorkersCompModal open={isOpen} close={() => setIsOpen(false)} />
    </div>
  )
}

export const WcManagementPanel = () => {
  const dispatch = useAppDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const payrollProfile = useReselector(selectPayrollProfile)
  useEffect(() => {
    dispatch(fetchPayrollProfileIfNeeded())
  }, [dispatch])

  if (payrollProfile?.wcProvider === NEXT) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col md:w-1/2">
          <Card color={CardColors.LEVEL_1}>
            <LogoRow />

            <br />

            <Text variant={TypographyStyles.H2}>
              View your Workers Compensation Plan
            </Text>

            <br />

            <Text variant={TypographyStyles.BODY_MD}>
              It looks like you are enrolled or have started enrollment in a
              Workers compensation plan with Next. Manage the status and view
              details of your Workers Compensation Plan.
            </Text>

            <br />

            <WorkersCompPanel buttonText="Manage your Plan" />
          </Card>
        </div>
      </div>
    )
  } else {
    return (
      <div className="flex flex-col gap-6">
        <div className="md:w-1/2">
          <Card color={CardColors.LEVEL_1}>
            <Text variant={TypographyStyles.H2}>
              You’re already enrolled in a workers’ compensation plan
            </Text>

            <br />

            <Text variant={TypographyStyles.BODY_MD}>
              If this isn’t correct, please let us know.
            </Text>

            <br />

            <Button variant="secondary" onClick={() => setIsOpen(true)}>
              Update
            </Button>

            <WorkersCompModal open={isOpen} close={() => setIsOpen(false)} />
          </Card>
        </div>
      </div>
    )
  }
}
/**
 *
 * This Panel is used only for WC Enrollment. This is also show on the Payroll Details tab _if_ a user has not enrolled AND they live in a state that allows Next WC
 *
 * @returns Enrollment Panel
 */
export const NextWcEnrollmentPanel = ({
  enrollmentRequirements,
  suppressIfEnrolled,
}: {
  enrollmentRequirements?: WcEnrollmentRequirement
  suppressIfEnrolled?: boolean
}) => {
  if (enrollmentRequirements?.show_enrollment_type !== 'default') return null
  if (!enrollmentRequirements?.show_enrollment && suppressIfEnrolled)
    return null
  if (!enrollmentRequirements?.show_enrollment) return <WcManagementPanel />
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Left Column */}
        <div className="w-full lg:w-1/2">
          <Card color={CardColors.EMPHASIS} className="h-full">
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="flex-1">
                <LogoRow />
                <Text variant={TypographyStyles.H2} className="mt-4">
                  Get Covered by Workers’ Compensation
                </Text>
                <br />
                <Text variant={TypographyStyles.BODY_MD}>
                  Workers&apos; compensation insurance protects and covers your
                  business against the unexpected while your employees are on
                  the job. Depending on your state, you may be required to
                  enroll in workers compensation.
                </Text>
              </div>
              <EnrollmentButtons />
            </div>
          </Card>
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-1/2">
          <Card color={CardColors.LEVEL_1} className="h-full">
            <div className="flex flex-col gap-4">
              <Text variant={TypographyStyles.H2}>
                Do I need workers&apos; compensation insurance?
              </Text>
              <Text>
                If you are the owner of your business and only employ yourself,
                you generally are not required to purchase workers compensation.
                However, if you have additional employees, workers compensation
                may be required.
              </Text>
              <Text>
                We encourage you to review your{' '}
                <Link newPage href={GUSTO_WC_ARTICLE}>
                  state requirements here
                </Link>
                .
              </Text>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
export const WorkersCompensationTab = () => {
  const [enrollmentRequirements, setEnrollmentRequirements] =
    useState<WcEnrollmentRequirement>()
  const company = useReselector(selectPayrollCompany)

  const dispatch = useAppDispatch()
  useEffect(() => {
    const fetchData = async () => {
      const res = await fetchPayrollWcEnrollmentRequirements()(dispatch)
      if (res) {
        setEnrollmentRequirements(res)
      }
      await Promise.all([dispatch(fetchPayrollProfileIfNeeded(false))])
    }

    fetchData()
  }, [dispatch])

  if (!company) {
    return null
  }

  if (enrollmentRequirements?.show_enrollment_type === GOVERNMENT) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column */}
          <div className="w-full lg:w-1/2">
            <Card color={CardColors.EMPHASIS}>
              <div className="flex flex-col gap-4">
                <Text variant={TypographyStyles.H2}>
                  Workers&apos; compensation in {enrollmentRequirements?.state}
                </Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  Workers&apos; compensation insurance protects and covers your
                  business against the unexpected while your employees are on
                  the job.
                </Text>
                {enrollmentRequirements?.state && (
                  <Text variant={TypographyStyles.BODY_MD}>
                    {stateToCopyMapping[enrollmentRequirements.state]
                      ? stateToCopyMapping[enrollmentRequirements.state]
                      : "This state's program is managed by the government and not a private carrier and therefore is not supported/paid by Heard or our workers' comp partner."}
                  </Text>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-1/2">
            <Card color={CardColors.LEVEL_1}>
              <div className="flex flex-col gap-4">
                <Text variant={TypographyStyles.H2}>
                  Do I need workers&apos; compensation insurance?
                </Text>
                <Text>
                  If you are the owner of your business and only employ
                  yourself, you generally are not required to purchase workers
                  compensation. However, if you have additional employees,
                  workers compensation may be required.
                </Text>
                <Text>
                  We encourage you to review your{' '}
                  <Link newPage href={GUSTO_WC_ARTICLE}>
                    state requirements here
                  </Link>
                  .
                </Text>
              </div>
            </Card>
          </div>
        </div>
      </div>
    )
  } else if (enrollmentRequirements?.show_enrollment_type === TEXAS) {
    return (
      <Card color={CardColors.LEVEL_1}>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-1/2 w-full">
            <Text variant={TypographyStyles.H2}>
              Workers&apos; Compensation may not be necessary
            </Text>
            <br />
            <Text variant={TypographyStyles.BODY_MD}>
              Workers&apos; compensation insurance protects and covers your
              business against the unexpected while your employees are on the
              job. Typically, workers compensation is not required in Texas
              unless you work for the government or in construction, but we
              encourage you to review your{' '}
              <Link newPage href={GUSTO_WC_ARTICLE}>
                state requirements here
              </Link>
              .
              <br />
              <br />
              You may still enroll in workers compensation if you would like.
            </Text>
          </div>
        </div>
        <EnrollmentButtons />
      </Card>
    )
  } else {
    return (
      <NextWcEnrollmentPanel enrollmentRequirements={enrollmentRequirements} />
    )
  }
}
